import type { RoleAssignment } from '@obraplan/core/domain/access/permission-resolver';
import { ROLE } from '@obraplan/core/domain/access/roles';
import { holdsSuperAdmin } from '@obraplan/core/domain/access/super-admin';

/**
 * O que a tela de perfis oferece, linha a linha (RN-286 a RN-290).
 *
 * É o espelho do que os casos de uso recusam — só esconde o botão que o servidor
 * recusaria de qualquer jeito (RN-134). O Administrador cuida das pessoas, menos do
 * titular; o titular se edita, mas não se desativa nem larga o papel por aqui.
 *
 * Fora do componente porque é onde as regras novas se cruzam: pessoa × papel × quem olha.
 */
export interface ProfileAccess {
  /** O perfil desta linha é o do Super administrador. */
  readonly isTitular: boolean;
  readonly canEdit: boolean;
  readonly canSetPassword: boolean;
  readonly canToggleActive: boolean;
  readonly canAssign: boolean;
  /** Pode revogar esta atribuição? O Super administrador nunca se revoga: só se transfere. */
  canRevoke(roleKey: string): boolean;
}

export function profileAccess(input: {
  readonly actorId: string;
  readonly targetId: string;
  readonly targetAssignments: readonly RoleAssignment[];
  readonly rolesById: ReadonlyMap<string, { readonly key: string }>;
  readonly canManageUsers: boolean;
  readonly canManageRoles: boolean;
}): ProfileAccess {
  const isTitular = holdsSuperAdmin(input.targetAssignments, input.rolesById);
  // Só o próprio titular mexe nele.
  const untouchable = isTitular && input.actorId !== input.targetId;
  const canAssign = (input.canManageUsers || input.canManageRoles) && !untouchable;

  return {
    isTitular,
    canEdit: input.canManageUsers && !untouchable,
    canSetPassword: input.canManageUsers && !untouchable,
    // Nem o titular se desativa: o papel nunca fica sem dono.
    canToggleActive: input.canManageUsers && !isTitular,
    canAssign,
    canRevoke: (roleKey) => canAssign && roleKey !== ROLE.SUPER_ADMIN,
  };
}
